import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown } from 'lucide-react';
import { candidateCellColumn } from './CandidateCell';
import RowActionsCell from './RowActionsCell';
import { filterActionsForRow } from '../actionConditions';
import SelectableTabTable from './SelectableTabTable';
import PipelineDataTable, { candidateShape } from './PipelineDataTable';
import TabEmptyState from './TabEmptyState';
import MatchDetailsPanel from './MatchDetailsPanel';

// Expand-chevron column — only prepended when `expandable: true` (New/
// Closed tabs, see MatchDetailsPanel.jsx). `expandedId` is closed over from
// the enclosing SeniorFlowTab's own local state purely to rotate the
// chevron for whichever single row is currently open; the actual
// expand/collapse click handling lives on the row itself (onRowClick below,
// passed to PipelineDataTable/SelectableTabTable → DataTable, which already
// skips any element under data-prevent-row-click="true" — see
// RowActionsCell.jsx), not on this column specifically.
const expandChevronColumn = (expandedId) => ({
  key: 'expand_chevron',
  header: '',
  className: 'text-sm',
  width: '32px',
  align: 'center',
  render: (_v, row) => (
    <span
      className="inline-flex items-center justify-center w-5 h-5 text-muted-foreground transition-transform duration-200"
      style={{
        transform: expandedId === row.candidate_id ? 'rotate(180deg)' : 'none',
      }}
    >
      <ChevronDown style={{ width: 14, height: 14, strokeWidth: 2 }} />
    </span>
  ),
});

// Shared shape for every senior_level_flow tab — no reference design exists
// for this flow (unlike secondary's screening-pipeline-v3 template), so
// each tab uses the same simple columns default's Pending/InProgress tabs
// use: candidate identity + a View-profile row action. "Updated on"
// (sharedColumns.js' mappedAtColumn) was here too, removed per explicit
// request across every senior_level_flow tab — see SeniorContactedTab.jsx
// for the one tab built outside this factory that also had to drop it.
// Selection (SelectableTabTable, checkboxes + the bulk action bar) is only
// mounted when `selectable` is true — AssignmentPipeline.jsx sets that to
// false while a merged tab (e.g. Failed, which also covers Not Interested/
// No Show/Non Responsive) has "All statuses" showing, since those rows
// don't all share one real status and no single manual_actions array could
// be safely bulk-applied across them. Narrowed to one specific sub-status
// (or a tab that never merges anything), it's selectable again and falls
// back to plain PipelineDataTable (no checkboxes/bulk bar) otherwise —
// same shell scorableTabFactory.jsx's tabs already use unconditionally.
//
// Each row's own kebab menu (RowActionsCell) is independent of that: its
// manual_actions come from `manualActionsByStatus[row.status]` — that
// candidate's own real status — falling back to the tab-level
// `manualActions` only if their status isn't in the map, so a "Not
// Interested" row inside the "Failed" tab never gets offered
// move_to_shortlisted just because the tab it's grouped under allows it.
//
// That same "All statuses" case is also the one place each row's own real
// status earns its keep — with a single sub-status picked (or selected),
// every visible row already shares one obvious status, but "All statuses"
// mixes real statuses together. Rather than a dedicated Status column (which
// only ever existed for this one case and ate into every other column's
// width while visible — confirmed live, it forced "Interview In Progress"
// onto two lines and left too little room for a longer row_actions primary
// button), that status now shows as a small colored line inside
// RowActionsCell, above its button row — see that file's own comment (it
// tried living next to the candidate's name first, but that cell was
// already the busiest one in the row).
// extraColumns: per-tab columns inserted right after the Candidate column —
// e.g. the New tab's "Location & Availability" column
// (locationAvailabilityColumn). A stable module-level array from the
// caller, so it's safe to leave out of the useMemo deps below.
export const createSeniorFlowTab = (
  status,
  emptyDescription,
  { extraColumns = [], expandable = false } = {}
) => {
  const SeniorFlowTab = ({
    candidates,
    isLoading,
    page,
    pageSize,
    totalPages,
    totalItems,
    onPageChange,
    onPageSizeChange,
    opportunityId,
    assignmentId,
    requirementLocationId,
    stateMachineKey,
    manualActions,
    manualActionsByStatus,
    selectable,
    onActionSuccess,
    onViewProfile,
  }) => {
    // Accordion state — which single row (by candidate_id) is open, or null.
    // Local to this tab instance: switching tabs/pages remounts it, so
    // nothing carries over stale (same behavior as CandidateMatchRow's own
    // expandedId in SubmitCandidate.jsx, this session's design reference).
    const [expandedId, setExpandedId] = useState(null);
    const toggleExpanded = (row) =>
      setExpandedId((cur) =>
        cur === row.candidate_id ? null : row.candidate_id
      );
    // Stable identity (useCallback) so this doesn't trip
    // react/no-unstable-nested-components — it's a render-prop (DataTable
    // calls it per row), not a component instantiated inline.
    const renderExpandedRow = useCallback(
      (row) => <MatchDetailsPanel matchDetails={row.match_details} />,
      []
    );

    const columns = useMemo(
      () => [
        ...(expandable ? [expandChevronColumn(expandedId)] : []),
        candidateCellColumn,
        ...extraColumns,
        {
          key: 'row_actions',
          header: '',
          // `relative` makes this <td> the containing block for
          // RowActionsCell's absolutely positioned status badge (rowStatus)
          // — see that file's own comment.
          className: 'text-sm relative',
          // The primary action button (whichever manual_action the row's
          // status flags primary_action: true, rendered outside the kebab —
          // see RowActionsCell.jsx) + kebab + chevron needs real room — left
          // unconstrained it would only get an equal share alongside every
          // other column under table-fixed, same issue as the rest. 210px
          // was enough back when this slot only ever held "View profile",
          // but some real primary-action labels (e.g. "Interview Profile
          // Update", "Complete Interview Round") run to 24+ characters and
          // overflowed into the next column's cell at that width —
          // confirmed live. 280px is wide enough for the longest label seen
          // live plus kebab + chevron. Matches SeniorContactedTab.jsx's own
          // row_actions width for consistency across every senior_level_flow
          // tab.
          width: '280px',
          // Left-aligned per explicit request — every senior_level_flow
          // column reads left-to-right like the Candidate column, for a
          // consistent layout across tabs. RowActionsCell's own flex row
          // still renders flush right internally (justify-end) regardless
          // of this column-level setting.
          align: 'left',
          render: (_v, row) => {
            const rowManualActions =
              manualActionsByStatus[row.status] || manualActions;
            // Each action's own `conditions` (GET .../flow-statuses) gate
            // it against this row's real data — e.g. acknowledge_interview
            // only while interview_details.requires_acknowledgment is true,
            // complete_interview_round only once the meeting has actually
            // ended. See actionConditions.js — generic, not specific to any
            // one action key.
            const filteredManualActions = filterActionsForRow(
              rowManualActions,
              row
            );
            return (
              <RowActionsCell
                candidateId={row.candidate_id}
                mappingId={row.id}
                status={status}
                // Only in a merged "All statuses" view — see the file header
                // comment and RowActionsCell.jsx's own comment on `rowStatus`
                // vs `status`.
                rowStatus={selectable ? undefined : row.status}
                opportunityId={opportunityId}
                assignmentId={assignmentId}
                requirementLocationId={requirementLocationId}
                stateMachineKey={stateMachineKey}
                manualActions={filteredManualActions}
                onActionSuccess={onActionSuccess}
                onViewProfile={onViewProfile}
              />
            );
          },
        },
      ],
      [
        expandable,
        expandedId,
        selectable,
        opportunityId,
        assignmentId,
        requirementLocationId,
        stateMachineKey,
        manualActions,
        manualActionsByStatus,
        onActionSuccess,
        onViewProfile,
      ]
    );

    const expandedRowIds = expandable && expandedId != null ? [expandedId] : [];

    if (!selectable) {
      return (
        <PipelineDataTable
          columns={columns}
          candidates={candidates}
          isLoading={isLoading}
          page={page}
          pageSize={pageSize}
          totalPages={totalPages}
          totalItems={totalItems}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          emptyState={<TabEmptyState description={emptyDescription} />}
          onRowClick={expandable ? toggleExpanded : undefined}
          expandable={expandable}
          expandedRowIds={expandedRowIds}
          expandedRowRender={expandable ? renderExpandedRow : undefined}
        />
      );
    }

    return (
      <SelectableTabTable
        columns={columns}
        candidates={candidates}
        isLoading={isLoading}
        page={page}
        pageSize={pageSize}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        opportunityId={opportunityId}
        assignmentId={assignmentId}
        stateMachineKey={stateMachineKey}
        manualActions={manualActions}
        onActionSuccess={onActionSuccess}
        emptyState={<TabEmptyState description={emptyDescription} />}
        onRowClick={expandable ? toggleExpanded : undefined}
        expandable={expandable}
        expandedRowIds={expandedRowIds}
        expandedRowRender={expandable ? renderExpandedRow : undefined}
      />
    );
  };

  SeniorFlowTab.propTypes = {
    candidates: PropTypes.arrayOf(candidateShape),
    isLoading: PropTypes.bool,
    page: PropTypes.number,
    pageSize: PropTypes.number,
    totalPages: PropTypes.number,
    totalItems: PropTypes.number,
    onPageChange: PropTypes.func,
    onPageSizeChange: PropTypes.func,
    opportunityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    assignmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    requirementLocationId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    stateMachineKey: PropTypes.string,
    manualActions: PropTypes.arrayOf(
      PropTypes.shape({ key: PropTypes.string, label: PropTypes.string })
    ),
    manualActionsByStatus: PropTypes.objectOf(
      PropTypes.arrayOf(
        PropTypes.shape({ key: PropTypes.string, label: PropTypes.string })
      )
    ),
    selectable: PropTypes.bool,
    onActionSuccess: PropTypes.func,
    onViewProfile: PropTypes.func,
  };

  SeniorFlowTab.defaultProps = {
    candidates: [],
    isLoading: false,
    page: 1,
    pageSize: 10,
    totalPages: 1,
    totalItems: 0,
    onPageChange: () => {},
    onPageSizeChange: () => {},
    opportunityId: undefined,
    assignmentId: undefined,
    requirementLocationId: undefined,
    stateMachineKey: 'senior_level_flow',
    manualActions: [],
    manualActionsByStatus: {},
    selectable: true,
    onActionSuccess: () => {},
    onViewProfile: () => {},
  };

  return SeniorFlowTab;
};
